import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Notebook, LineChart, Settings, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import CapAssistant from "@/components/CapAssistant";

const links = [
  { to: "/", label: "Aujourd'hui", icon: LayoutDashboard, testId: "nav-today" },
  { to: "/history", label: "Journal", icon: Notebook, testId: "nav-history" },
  { to: "/learned", label: "Analyse", icon: LineChart, testId: "nav-learned" },
  { to: "/settings", label: "Réglages", icon: Settings, testId: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-app text-app grid-noise">
      <header
        className="sticky top-0 z-40 backdrop-blur-xl border-b border-line"
        style={{ backgroundColor: "color-mix(in oklab, var(--bg) 75%, transparent)" }}
        data-testid="app-header"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            className="flex items-center gap-2"
            onClick={() => navigate("/")}
            data-testid="brand-link"
          >
            <div className="w-7 h-7 rounded-md border border-line-strong flex items-center justify-center">
              <span className="font-display font-bold text-invest text-sm">G</span>
            </div>
            <div className="font-display font-semibold text-[15px] tracking-tight text-app">
              GeniCap
            </div>
            <div className="ml-2 hidden sm:block text-[10px] uppercase tracking-widest text-muted-2">
              journal quantitatif
            </div>
          </button>
          <div className="flex items-center gap-3">
            {user && (
              <div
                className="hidden sm:block text-xs font-mono-num text-muted-1"
                data-testid="user-email"
              >
                {user.email}
              </div>
            )}
            <button
              onClick={logout}
              className="text-xs text-muted-1 hover:text-app transition-colors duration-200 flex items-center gap-1.5"
              data-testid="logout-btn"
            >
              <LogOut size={14} /> <span className="hidden sm:inline">Sortir</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-32">
        {children}
      </main>

      <ScrollToTop />
      <CapAssistant />

      {/* Bottom nav — bigger touch targets on mobile */}
      <nav
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 backdrop-blur-xl border rounded-full px-2 py-2 flex items-center gap-1 shadow-2xl"
        style={{
          backgroundColor: "color-mix(in oklab, var(--card) 90%, transparent)",
          borderColor: "var(--border-strong)",
        }}
        data-testid="bottom-nav"
      >
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/"}
            data-testid={l.testId}
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 rounded-full text-xs transition-colors duration-200 ${
                isActive
                  ? "btn-primary"
                  : "text-muted-1 hover:text-app hover:bg-card-2"
              }`
            }
          >
            <l.icon className="w-[18px] h-[18px] sm:w-[14px] sm:h-[14px]" />
            <span className="hidden sm:inline">{l.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
